import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { AdminCostReport, CostSpend } from '@/lib/types'
import { PageHeader } from '@/components/AppLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn, formatMicros, formatNumber } from '@/lib/utils'
import { ErrorNote } from './DashboardPage'

const WINDOWS = [7, 30, 90] as const

/** Per-image figures are fractions of a dollar; four places keep configurations comparable. */
const PER_IMAGE_DIGITS = 4

export function CostsPage() {
  const [days, setDays] = useState<number>(30)

  const { data, isLoading, error } = useQuery({
    queryKey: ['costs', days],
    queryFn: () => api.get<AdminCostReport>('api/admin/costs', { days }),
  })

  const failedShare =
    data && data.total.costMicros > 0 ? data.total.failedCostMicros / data.total.costMicros : undefined

  return (
    <>
      <PageHeader
        title="Costs"
        description="What a generated image costs to make, what drives that cost, and what it earns."
        actions={
          <select
            className="h-9 rounded-lg border bg-[var(--color-surface)] px-3 text-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {WINDOWS.map((window) => (
              <option key={window} value={window}>
                Last {window} days
              </option>
            ))}
          </select>
        }
      />

      <div className="space-y-6 p-8">
        {error && <ErrorNote message={(error as Error).message} />}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Figure
            label="Cost per image"
            value={isLoading ? '—' : formatMicros(data?.total.costPerImageMicros, PER_IMAGE_DIGITS)}
            sub={data ? `${formatNumber(data.total.imagesDelivered)} images delivered` : undefined}
          />
          <Figure
            label="Model spend"
            value={isLoading ? '—' : formatMicros(data?.total.costMicros)}
            sub={data ? `${formatNumber(data.total.jobs)} generations` : undefined}
          />
          <Figure
            label="Spent on failed jobs"
            value={isLoading ? '—' : formatMicros(data?.total.failedCostMicros)}
            sub={failedShare !== undefined ? `${formatPercent(failedShare)} of spend` : undefined}
          />
          <Figure
            label="Margin per user image"
            value={isLoading ? '—' : formatMicros(data?.users.marginPerImageMicros, PER_IMAGE_DIGITS)}
            valueClassName={marginTone(data?.users.marginPerImageMicros)}
            sub={
              data?.creditValueMicros != null
                ? `At ${formatMicros(data.creditValueMicros, PER_IMAGE_DIGITS)} per credit`
                : 'Set CREDITS_VALUE_MICROS to estimate'
            }
          />
        </section>

        <Card>
          <CardHeader>
            <CardTitle>By source</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Images</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">On failed jobs</TableHead>
                  <TableHead className="text-right">Per image</TableHead>
                  <TableHead className="text-right">Credits kept</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Margin per image</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data && (
                  <>
                    <SourceRow label="Users" spend={data.users} />
                    <SourceRow label="Curated" spend={data.curated} />
                    <SourceRow label="Total" spend={data.total} strong />
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By configuration</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead>Quality</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead className="text-right">Images</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Per image</TableHead>
                  <TableHead className="text-right">Avg output tokens</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.byConfiguration.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-[var(--color-text-tertiary)]">
                      No spend in this window.
                    </TableCell>
                  </TableRow>
                )}
                {data?.byConfiguration.map((row) => (
                  <TableRow key={`${row.model}|${row.quality}|${row.size}`}>
                    <TableCell>
                      {/* Jobs from before per-image costing carry a flat-rate
                          cost but no record of what they were billed on. */}
                      {row.model ?? <span className="text-[var(--color-text-tertiary)]">Before per-image costing</span>}
                    </TableCell>
                    <TableCell className="text-[var(--color-text-secondary)]">{row.quality ?? '—'}</TableCell>
                    <TableCell className="text-[var(--color-text-secondary)]">{row.size ?? '—'}</TableCell>
                    <TableCell className="tabular text-right">{formatNumber(row.imagesDelivered)}</TableCell>
                    <TableCell className="tabular text-right">{formatMicros(row.costMicros)}</TableCell>
                    <TableCell className="tabular text-right">
                      {formatMicros(row.costPerImageMicros, PER_IMAGE_DIGITS)}
                    </TableCell>
                    <TableCell className="tabular text-right text-[var(--color-text-secondary)]">
                      {formatNumber(row.averageOutputTokens)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <p className="text-xs text-[var(--color-text-tertiary)]">
          Each image is priced from the token usage OpenAI reports, at the configured per-token rates. "Per image"
          divides all spend — including jobs that failed after the provider had already billed — by the images
          actually delivered. Revenue assumes every kept credit was paid for, so it overstates income from free
          signup credits.
        </p>
      </div>
    </>
  )
}

function SourceRow({ label, spend, strong = false }: { label: string; spend: CostSpend; strong?: boolean }) {
  return (
    <TableRow className={strong ? 'font-medium' : undefined}>
      <TableCell>{label}</TableCell>
      <TableCell className="tabular text-right">{formatNumber(spend.imagesDelivered)}</TableCell>
      <TableCell className="tabular text-right">{formatMicros(spend.costMicros)}</TableCell>
      <TableCell className="tabular text-right">{formatMicros(spend.failedCostMicros)}</TableCell>
      <TableCell className="tabular text-right">{formatMicros(spend.costPerImageMicros, PER_IMAGE_DIGITS)}</TableCell>
      <TableCell className="tabular text-right">{formatNumber(spend.creditsCharged)}</TableCell>
      <TableCell className="tabular text-right">{formatMicros(spend.revenueMicros)}</TableCell>
      <TableCell className={cn('tabular text-right', marginTone(spend.marginPerImageMicros))}>
        {formatMicros(spend.marginPerImageMicros, PER_IMAGE_DIGITS)}
      </TableCell>
    </TableRow>
  )
}

function Figure({
  label,
  value,
  sub,
  valueClassName,
}: {
  label: string
  value: string
  sub?: string
  valueClassName?: string
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={cn('tabular text-2xl font-semibold', valueClassName)}>{value}</p>
        {sub && <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">{sub}</p>}
      </CardContent>
    </Card>
  )
}

/** A loss per image is the one figure on this page that needs to jump out. */
function marginTone(margin: number | undefined): string | undefined {
  return margin != null && margin < 0 ? 'text-[var(--color-danger)]' : undefined
}

function formatPercent(share: number): string {
  return new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 }).format(share)
}
